import { useEffect, useRef, useState } from "react";
import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  type Category,
} from "../../common/commonConstant";
import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import { useMaster } from "../../common/MasterContext";
import type { SearchCondition } from "./bop020w020";
import type { ExcludeCondition } from "../navigationState";

interface BOP020W020TemplateProps {
  type: Category;
  isLoading: boolean;
  categoryLarge: string;
  categorySmall: string;
  amountFrom: string;
  amountTo: string;
  dateFrom: string;
  dateTo: string;
  memo: string;
  recentSearches: SearchCondition[];
  results: any[];
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
  sortKey: string;
  sortDirection: "asc" | "desc";
  allChecked: boolean;
  isRowChecked: (row: any) => boolean;
  onTypeChange: (newType: Category) => void;
  onCategoryLargeChange: (value: string) => void;
  onCategorySmallChange: (value: string) => void;
  onAmountFromChange: (value: string) => void;
  onAmountToChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onMemoChange: (value: string) => void;
  keihiFlg: "" | "0" | "1";
  onKeihiFlgChange: (value: "" | "0" | "1") => void;
  mikakuteiFlg: "" | "0" | "1";
  onMikakuteiFlgChange: (value: "" | "0" | "1") => void;
  excludeConditions: ExcludeCondition[];
  onExcludeConditionChange: (
    index: number,
    field: keyof ExcludeCondition,
    value: string,
  ) => void;
  onClear: () => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onRecentSearchClick: (condition: SearchCondition) => void;
  onRecentSearchesTrim: (count: number) => void;
  onDetailClick: (row: any) => void;
  onItemsPerPageChange: (value: number) => void;
  onPageChange: (page: number) => void;
  onSort: (key: string) => void;
  onToggleAll: (checked: boolean) => void;
  onToggleRow: (id: string | number, checked: boolean) => void;
  onDelete: () => void;
  showResults: boolean;
}

export const BOP020W020Template = ({
  type,
  isLoading,
  categoryLarge,
  categorySmall,
  amountFrom,
  amountTo,
  dateFrom,
  dateTo,
  memo,
  recentSearches,
  results,
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  sortKey,
  sortDirection,
  allChecked,
  isRowChecked,
  onTypeChange,
  onCategoryLargeChange,
  onCategorySmallChange,
  onAmountFromChange,
  onAmountToChange,
  onDateFromChange,
  onDateToChange,
  onMemoChange,
  keihiFlg,
  onKeihiFlgChange,
  mikakuteiFlg,
  onMikakuteiFlgChange,
  excludeConditions,
  onExcludeConditionChange,
  onClear,
  onBack,
  onSubmit,
  onRecentSearchClick,
  onRecentSearchesTrim,
  onDetailClick,
  onItemsPerPageChange,
  onPageChange,
  onSort,
  onToggleAll,
  onToggleRow,
  onDelete,
  showResults,
}: BOP020W020TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();
  const { categoryLargeList, categorySmallList } = useMaster();
  const recentSearchListRef = useRef<HTMLDivElement>(null);
  // 除外条件のアコーディオンは初期状態で閉じておき、直近の検索条件の復元などで
  // 除外条件が設定された場合は内容が見えるよう自動的に開く
  const [isExcludeOpen, setIsExcludeOpen] = useState(false);

  // 設定済みの除外条件を「項目（大）/項目（小）」の文字列にする（閉じた見出し・直近の検索条件の表示用）
  const formatExcludeConditions = (conditions: ExcludeCondition[]) =>
    conditions
      .filter((condition) => condition.categoryLarge)
      .map((condition) =>
        [condition.categoryLarge, condition.categorySmall]
          .filter(Boolean)
          .join("/"),
      )
      .join("、");

  const excludeSummary = formatExcludeConditions(excludeConditions);

  useEffect(() => {
    if (excludeSummary) {
      setIsExcludeOpen(true);
    }
  }, [excludeSummary]);

  const handleDateInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.showPicker?.();
  };

  const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") return;
    e.preventDefault();
  };

  const formatRange = (from: string, to: string, suffix = "") => {
    if (from && to) return `${from}${suffix} - ${to}${suffix}`;
    if (from) return `${from}${suffix}以上`;
    if (to) return `${to}${suffix}以下`;
    return "";
  };

  const keihiLabel = (flg: "" | "0" | "1") => {
    if (flg === "1") return "対象";
    if (flg === "0") return "対象外";
    return "";
  };

  const mikakuteiLabel = (flg: "" | "0" | "1") => {
    if (flg === "1") return "未確定";
    if (flg === "0") return "確定済";
    return "";
  };

  const buildRecentSearchSummary = (condition: SearchCondition) => {
    const parts = [
      condition.type,
      condition.categoryLarge,
      condition.categorySmall,
      formatRange(condition.amountFrom, condition.amountTo, "円"),
      formatRange(condition.dateFrom, condition.dateTo),
      condition.memo,
      keihiLabel(condition.keihiFlg ?? ""),
      mikakuteiLabel(condition.mikakuteiFlg ?? ""),
      formatExcludeConditions(condition.excludeConditions ?? [])
        ? `除外:${formatExcludeConditions(condition.excludeConditions ?? [])}`
        : "",
    ].filter(Boolean);

    return parts.join(" / ");
  };

  const formatAmount = (amount: any) => {
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount)) {
      return "-";
    }
    return numericAmount.toLocaleString();
  };

  const formatKeihiFlg = (row: any) =>
    row.keihiFlg === 1 || row.keihiFlg === "1" ? "対象" : "対象外";

  const formatMikakuteiFlg = (row: any) =>
    row.mikakuteiFlg === 1 || row.mikakuteiFlg === "1" ? "未確定" : "確定済";

  const paginationInfo = (
    <div className="pagination-info">
      <span>
        全{totalItems}件中 {(currentPage - 1) * itemsPerPage + 1}-
        {Math.min(currentPage * itemsPerPage, totalItems)}件を表示
      </span>
    </div>
  );

  const paginationControls = (
    <div className="pagination-controls">
      <label className="items-per-page-label">
        表示件数:
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="items-per-page-select"
        >
          <option value={10}>10</option>
          <option value={30}>30</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </label>

      <div className="page-navigation">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="page-button"
        >
          前へ
        </button>

        <span className="page-info">
          {currentPage} / {totalPages}
        </span>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="page-button"
        >
          次へ
        </button>
      </div>
    </div>
  );

  useEffect(() => {
    const list = recentSearchListRef.current;
    if (!list || recentSearches.length === 0) return;

    const trimOverflowItems = () => {
      const chips = Array.from(list.children) as HTMLElement[];
      if (chips.length === 0) return;

      const listWidth = list.clientWidth;
      const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;
      let usedWidth = 0;
      let fitCount = 0;

      chips.forEach((chip, index) => {
        const nextWidth = usedWidth + chip.offsetWidth + (index > 0 ? gap : 0);
        if (nextWidth <= listWidth || fitCount === 0) {
          usedWidth = nextWidth;
          fitCount += 1;
        }
      });

      if (fitCount < recentSearches.length) {
        onRecentSearchesTrim(fitCount);
      }
    };

    const animationFrame = window.requestAnimationFrame(trimOverflowItems);
    const resizeObserver = new ResizeObserver(trimOverflowItems);
    resizeObserver.observe(list);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [onRecentSearchesTrim, recentSearches]);

  return (
    <div
      className={`bop020w020-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      {isLoading && (
        <div className="bop020w020-loading-overlay">
          <Spinner label="処理中..." />
        </div>
      )}
      <div className="screen-title">収支一覧画面</div>
      {/* 検索条件 */}
      <div className="search-layout">
        <details className="search-details" open>
          <summary>検索条件</summary>
          <form id="searchForm" onSubmit={onSubmit}>
            <div className="form-columns">
              <div className="form-column">
                <div className="form-row">
                  <label className="form-label">収支</label>

                  <div className="radio-group">
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="type"
                        checked={type === INCOME_TYPE}
                        onChange={() => onTypeChange(INCOME_TYPE)}
                      />
                      収入
                    </label>

                    <label className="radio-item">
                      <input
                        type="radio"
                        name="type"
                        checked={type === EXPENSE_TYPE}
                        onChange={() => onTypeChange(EXPENSE_TYPE)}
                      />
                      支出
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <label className="form-label">項目（大）</label>
                  <select
                    value={categoryLarge}
                    onChange={(e) => onCategoryLargeChange(e.target.value)}
                  >
                    <option value="">選択してください</option>
                    {categoryLargeList
                      .filter((item) => item.type === type)
                      .map((item) => (
                        <option key={item.label} value={item.category}>
                          {item.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-row">
                  <label className="form-label">項目（小）</label>
                  <select
                    value={categorySmall}
                    onChange={(e) => onCategorySmallChange(e.target.value)}
                  >
                    <option value="">選択してください</option>
                    {categorySmallList
                      .filter((item) => item.category === categoryLarge)
                      .map((item) => (
                        <option key={item.label} value={item.label}>
                          {item.label}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="form-row">
                  <label className="form-label">金額（円）</label>
                  <div className="range-field">
                    <input
                      type="number"
                      value={amountFrom}
                      onChange={(e) => onAmountFromChange(e.target.value)}
                    />
                    <span className="range-separator">～</span>
                    <input
                      type="number"
                      value={amountTo}
                      onChange={(e) => onAmountToChange(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="form-column">
                <div className="form-row">
                  <label className="form-label">日付</label>
                  <div className="range-field">
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => onDateFromChange(e.target.value)}
                      onClick={handleDateInputClick}
                      onKeyDown={handleDateInputKeyDown}
                    />
                    <span className="range-separator">～</span>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => onDateToChange(e.target.value)}
                      onClick={handleDateInputClick}
                      onKeyDown={handleDateInputKeyDown}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <label className="form-label">備考</label>
                  <input
                    type="text"
                    value={memo}
                    onChange={(e) => onMemoChange(e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <label className="form-label">経費</label>
                  <div className="radio-group">
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="keihiFlg"
                        checked={keihiFlg === ""}
                        onChange={() => onKeihiFlgChange("")}
                        disabled={type === INCOME_TYPE}
                      />
                      指定なし
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="keihiFlg"
                        checked={keihiFlg === "1"}
                        onChange={() => onKeihiFlgChange("1")}
                        disabled={type === INCOME_TYPE}
                      />
                      対象
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="keihiFlg"
                        checked={keihiFlg === "0"}
                        onChange={() => onKeihiFlgChange("0")}
                        disabled={type === INCOME_TYPE}
                      />
                      対象外
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <label className="form-label">未確定</label>
                  <div className="radio-group">
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="mikakuteiFlg"
                        checked={mikakuteiFlg === ""}
                        onChange={() => onMikakuteiFlgChange("")}
                      />
                      指定なし
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="mikakuteiFlg"
                        checked={mikakuteiFlg === "1"}
                        onChange={() => onMikakuteiFlgChange("1")}
                      />
                      未確定
                    </label>
                    <label className="radio-item">
                      <input
                        type="radio"
                        name="mikakuteiFlg"
                        checked={mikakuteiFlg === "0"}
                        onChange={() => onMikakuteiFlgChange("0")}
                      />
                      確定済
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* 除外条件（任意）: 指定した項目（大）・項目（小）に該当する行を検索結果から除く */}
            <details
              className="exclude-section"
              open={isExcludeOpen}
              onToggle={(e) => setIsExcludeOpen(e.currentTarget.open)}
            >
              <summary className="exclude-section-title">
                除外条件
                {!isExcludeOpen && excludeSummary && (
                  <span className="exclude-section-summary">
                    （{excludeSummary}）
                  </span>
                )}
              </summary>
              <div className="exclude-section-content">
                <div className="exclude-section-note">
                  いずれかの条件に該当する行を除外します
                </div>
                {excludeConditions.map((condition, index) => (
                  <div key={index} className="exclude-condition">
                    <div className="exclude-condition-label">
                      条件{index + 1}
                    </div>
                    <div className="form-columns">
                      <div className="form-column">
                        <div className="form-row">
                          <label className="form-label">項目（大）</label>
                          <select
                            value={condition.categoryLarge}
                            aria-label={`除外条件${index + 1}の項目（大）`}
                            onChange={(e) =>
                              onExcludeConditionChange(
                                index,
                                "categoryLarge",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">選択してください</option>
                            {categoryLargeList
                              .filter((item) => item.type === type)
                              .map((item) => (
                                <option key={item.label} value={item.category}>
                                  {item.label}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                      <div className="form-column">
                        <div className="form-row">
                          <label className="form-label">項目（小）</label>
                          <select
                            value={condition.categorySmall}
                            aria-label={`除外条件${index + 1}の項目（小）`}
                            onChange={(e) =>
                              onExcludeConditionChange(
                                index,
                                "categorySmall",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">選択してください</option>
                            {categorySmallList
                              .filter(
                                (item) =>
                                  item.category === condition.categoryLarge,
                              )
                              .map((item) => (
                                <option key={item.label} value={item.label}>
                                  {item.label}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          </form>
        </details>
        <section className="recent-search-panel" aria-label="直近の検索条件">
          <div className="recent-search-title">直近の検索条件</div>
          {recentSearches.length > 0 ? (
            <div className="recent-search-list" ref={recentSearchListRef}>
              {recentSearches.map((condition, index) => {
                const summary = buildRecentSearchSummary(condition);

                return (
                  <button
                    key={`${summary}-${index}`}
                    type="button"
                    className="recent-search-chip"
                    onClick={() => onRecentSearchClick(condition)}
                    title={summary}
                  >
                    {summary}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="recent-search-empty">
              検索すると、ここに条件が表示されます。
            </div>
          )}
        </section>
      </div>
      <div className="action-panel">
        <div className="action-group action-group-left">
          <button type="button" className="clear-button" onClick={onBack}>
            戻る
          </button>
          <button
            type="button"
            className="clear-button"
            onClick={onClear}
            disabled={isLoading}
          >
            クリア
          </button>
          <button
            type="button"
            className="delete-button"
            onClick={onDelete}
            disabled={isLoading}
          >
            削除
          </button>
        </div>
        <div className="action-group action-group-right">
          <button
            form="searchForm"
            type="submit"
            className="search-button"
            disabled={isLoading}
          >
            検索
          </button>
        </div>
      </div>
      {/* 検索結果 */}
      {showResults && (
        <details open>
          <summary>検索結果</summary>

          {/* ページネーションコントロール（上部） */}
          {totalItems > 0 && (
            <div className="pagination-container pagination-container-top">
              {paginationInfo}
              {paginationControls}
            </div>
          )}

          {totalItems === 0 ? (
            <div className="no-results-message">検索結果は0件です</div>
          ) : deviceModeFlg ? (
            <div className="result-card-list" role="list">
              <div className="result-card-select-all">
                <label>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => onToggleAll(e.target.checked)}
                    aria-label="全選択"
                  />
                  全選択
                </label>
              </div>
              {results.map((row) => (
                <article
                  key={row.__rowId}
                  className="result-card"
                  role="listitem"
                >
                  <div className="result-card-header">
                    <label className="row-check">
                      <input
                        type="checkbox"
                        checked={isRowChecked(row)}
                        onChange={(e) =>
                          onToggleRow(row.__rowId, e.target.checked)
                        }
                        aria-label={`行選択-${row.__rowId}`}
                      />
                      選択
                    </label>
                    <span className="row-type">{row.type || "-"}</span>
                  </div>

                  <dl className="result-card-grid">
                    <div>
                      <dt>項目（大）</dt>
                      <dd>{row.categoryLarge || "-"}</dd>
                    </div>
                    <div>
                      <dt>項目（小）</dt>
                      <dd>{row.categorySmall || "-"}</dd>
                    </div>
                    <div>
                      <dt>金額（円）</dt>
                      <dd className={Number(row.amount) < 0 ? "minus" : ""}>
                        {formatAmount(row.amount)}
                      </dd>
                    </div>
                    <div>
                      <dt>日付</dt>
                      <dd>{row.date || "-"}</dd>
                    </div>
                    <div>
                      <dt>経費</dt>
                      <dd>{formatKeihiFlg(row)}</dd>
                    </div>
                    <div>
                      <dt>未確定</dt>
                      <dd>{formatMikakuteiFlg(row)}</dd>
                    </div>
                    <div>
                      <dt>更新日時</dt>
                      <dd>{row.updated || "-"}</dd>
                    </div>
                    <div className="memo-cell">
                      <dt>備考</dt>
                      <dd>{row.memo || "-"}</dd>
                    </div>
                  </dl>

                  <button
                    className="detail-button result-card-edit"
                    onClick={() => onDetailClick(row)}
                  >
                    編集
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="table-scroll-container">
              <table className="result-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={(e) => onToggleAll(e.target.checked)}
                        aria-label="全選択"
                      />
                    </th>
                    <th>収支</th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("categoryLarge")}
                    >
                      項目（大）
                      {sortKey === "categoryLarge" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("categorySmall")}
                    >
                      項目（小）
                      {sortKey === "categorySmall" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("amount")}
                    >
                      金額（円）
                      {sortKey === "amount" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("date")}
                    >
                      日付
                      {sortKey === "date" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th>備考</th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("keihiFlg")}
                    >
                      経費
                      {sortKey === "keihiFlg" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="sortable-header"
                      onClick={() => onSort("mikakuteiFlg")}
                    >
                      未確定
                      {sortKey === "mikakuteiFlg" && (
                        <span className="sort-indicator">
                          {sortDirection === "asc" ? " ▲" : " ▼"}
                        </span>
                      )}
                    </th>
                    <th>更新日時</th>
                    <th>編集</th>
                  </tr>
                </thead>

                <tbody>
                  {results.map((row) => (
                    <tr key={row.__rowId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isRowChecked(row)}
                          onChange={(e) =>
                            onToggleRow(row.__rowId, e.target.checked)
                          }
                          aria-label={`行選択-${row.__rowId}`}
                        />
                      </td>
                      <td>{row.type}</td>
                      <td>{row.categoryLarge}</td>
                      <td>{row.categorySmall}</td>
                      <td
                        style={{
                          color: row.amount < 0 ? "red" : "inherit",
                          textAlign: "right",
                        }}
                      >
                        {formatAmount(row.amount)}
                      </td>
                      <td>{row.date}</td>
                      <td>{row.memo}</td>
                      <td>{formatKeihiFlg(row)}</td>
                      <td>{formatMikakuteiFlg(row)}</td>
                      <td>{row.updated}</td>
                      <td className="detail-cell">
                        <button
                          className="detail-button"
                          onClick={() => onDetailClick(row)}
                        >
                          編集
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ページネーションコントロール（下部） */}
          {totalItems > 0 && (
            <div className="pagination-container pagination-container-bottom">
              {paginationControls}
            </div>
          )}
        </details>
      )}
    </div>
  );
};

export default BOP020W020Template;
